import React, { useMemo, useState } from "react";
import { Button, Form } from "antd";
import { useQuery } from "@apollo/client";

import { GET_DAMAGE_REPORTS_FILTER_OPTIONS } from "graphql/query/damages-gql";

import { useMe } from "components/use-hooks";
import Localize from "components/service/localize";
import DamageFields from "../fields";
import {
    getDamageFilterInitialValues,
    buildDamageFilterState,
    getDamageActiveFiltersCount
} from "../helpers/damage-filter-config";

const toUniqueSorted = (values) => {
    return [...new Set(values.filter(Boolean))]
        .sort((a, b) => String(a).localeCompare(String(b)));
};

const DamageFilterForm = ({ filter, callback }) => {
    const [form] = Form.useForm();
    const { me } = useMe();
    const { data, set, reset } = filter;
    const initialValues = getDamageFilterInitialValues(data);
    const [changedFieldsCount, setChangedFieldsCount] = useState(data.activeFilters);

    const { data: optionsData, loading } = useQuery(GET_DAMAGE_REPORTS_FILTER_OPTIONS, {
        variables: {
            where: {
                AND: [
                    {
                        column: "STATUS",
                        operator: "NOT_IN",
                        value: ["delete"]
                    },
                    {
                        column: "EE_ID",
                        operator: "EQ",
                        value: me.eeCurrentId
                    }
                ]
            },
            orderBy: [{ column: "CREATED_AT", order: "DESC" }],
            first: 500
        },
        fetchPolicy: "cache-first"
    });

    const {
        damageReportsCursor: {
            edges = []
        } = {}
    } = optionsData || {};

    const { hallOptions, blockOptions, standNumberOptions } = useMemo(() => {
        const stands = edges.map(({ node }) => node?.stand || {});

        return {
            hallOptions: toUniqueSorted(stands.map(({ hall_name }) => hall_name)),
            blockOptions: toUniqueSorted(stands.map(({ block }) => block)),
            standNumberOptions: toUniqueSorted(stands.map(({ stand_number }) => stand_number))
        };
    }, [edges]);

    return (
        <Form
            key="damage-filter-form"
            form={form}
            layout="vertical"
            className="damage-filter-form"
            onFinish={(values) => {
                set(buildDamageFilterState(values, data));
                callback();
            }}
            onValuesChange={(_, allValues) => {
                setChangedFieldsCount(getDamageActiveFiltersCount(allValues));
            }}
        >
            <Form.Item
                name="damageStatus"
                label={<Localize>FORMS.Input_Label_DamageStatus</Localize>}
                className="dark"
                initialValue={initialValues.damageStatus}
            >
                <DamageFields.StatusSelect />
            </Form.Item>
            <Form.Item
                name="hallName"
                label={<Localize>GLOBAL.Card_Hero_Hall</Localize>}
                className="dark"
                initialValue={initialValues.hallName}
            >
                <DamageFields.HallSelect
                    options={hallOptions}
                    loading={loading}
                    allowClear
                />
            </Form.Item>
            <Form.Item
                name="block"
                label={<Localize>GLOBAL.Card_Hero_Block</Localize>}
                className="dark"
                initialValue={initialValues.block}
            >
                <DamageFields.BlockSelect
                    options={blockOptions}
                    loading={loading}
                    allowClear
                />
            </Form.Item>
            <Form.Item
                name="standNumber"
                label={<Localize>GLOBAL.Card_Hero_Stand</Localize>}
                className="dark"
                initialValue={initialValues.standNumber}
            >
                <DamageFields.StandNumberSelect
                    options={standNumberOptions}
                    loading={loading}
                    allowClear
                />
            </Form.Item>
            <div className="form-actions">
                {!changedFieldsCount ? (
                    <Button
                        className="dark"
                        type="default"
                        onClick={callback}
                    >
                        <Localize wrap>GLOBAL.Button_Text_Cancel</Localize>
                    </Button>
                ) : (
                    <>
                        <Button
                            className="dark"
                            type="default"
                            onClick={() => {
                                reset();
                                callback();
                            }}
                        >
                            <Localize wrap>FORMS.Button_Text_ResetFilters</Localize>
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                        >
                            <Localize wrap>FORMS.Button_Text_ApplyFilters</Localize>
                        </Button>
                    </>
                )}
            </div>
        </Form>
    );
};

export default DamageFilterForm;
